import React, { useState, useEffect, useRef } from 'react';
import { useGetProfileQuery, useUpdateProfileMutation, useChangePasswordMutation } from '../redux/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { initializeAuth } from '../redux/authSlice';
import SettingsHeader from '../components/SettingsHeader';
import SettingsMessages from '../components/SettingsMessages';
import SettingsSidebar from '../components/SettingsSidebar';
import SettingsContent from '../components/SettingsContent';
import SettingsLoading from '../components/SettingsLoading';

const ProfileSettings = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [authInitialized, setAuthInitialized] = useState(false);
  const [newExpertise, setNewExpertise] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [cvFile, setCvFile] = useState(null);

  const fileInputRef = useRef(null);
  const cvFileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { accessToken, isAuthenticated } = useSelector(state => state.auth);

  // Initialize auth on component mount
  useEffect(() => {
    console.log('🔄 Initializing auth state...');
    dispatch(initializeAuth());
    setAuthInitialized(true);
  }, [dispatch]);

  // Fetch profile data using RTK Query - only if authenticated AND auth is initialized
  const { 
    data: profileResponse, 
    isLoading, 
    error, 
    refetch,
    isError 
  } = useGetProfileQuery(undefined, {
    skip: !isAuthenticated || !authInitialized,
  });

  // Update profile mutation
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  // Change password mutation
  const [changePassword, { isLoading: isPasswordChanging }] = useChangePasswordMutation();

  // Profile data state - updated to match API response structure
  const [profileData, setProfileData] = useState({
    personal: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      bio: '',
    },
    professional: {
      subjects: '', // Will be comma-separated string from Department array
      subjectsArray: [], // Store the full Department array objects
      yearsOfExperience: '',
      areaOfExpertise: '', // Will be comma-separated string from Expertise array
      expertiseArray: [], // Store the full Expertise array objects
      website: '',
      videoUrl: '',
      cvUrl: '' // Store CV URL
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  // Update profile data when API response is received
  useEffect(() => {
    if (profileResponse?.status === true && profileResponse.data) {
      const apiData = profileResponse.data;
      console.log('✅ Profile data received from API:', apiData);
      
      // Handle Department array - API returns as "Department" with capital D
      let subjectsValue = '';
      let subjectsArray = [];
      if (apiData.Department && Array.isArray(apiData.Department)) {
        subjectsArray = apiData.Department;
        subjectsValue = apiData.Department.map(dept => dept.name).join(', ');
        console.log('📚 Subjects from API:', subjectsValue);
      }
      
      // Handle Expertise array - API returns as "Expertise" with capital E
      let expertiseValue = '';
      let expertiseArray = [];
      if (apiData.Expertise && Array.isArray(apiData.Expertise)) {
        expertiseArray = apiData.Expertise;
        expertiseValue = apiData.Expertise.map(exp => exp.name).join(', ');
        console.log('🎯 Expertise from API:', expertiseValue);
      }
      
      setProfileData({
        personal: {
          firstName: apiData.fname || '',
          lastName: apiData.lname || '',
          email: apiData.email || '',
          phone: apiData.phoneNum || '',
          bio: apiData.bio || '',
        },
        professional: {
          subjects: subjectsValue,
          subjectsArray: subjectsArray, // Store full objects for future use
          yearsOfExperience: apiData.yearsOfExperience?.toString() || '',
          areaOfExpertise: expertiseValue,
          expertiseArray: expertiseArray, // Store full objects for future use
          website: apiData.website || '',
          videoUrl: apiData.videoUrl || '',
          cvUrl: apiData.cvUrl || '' // Store CV URL
        },
        security: {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
      });

      // Set profile image preview
      if (apiData.image) {
        setProfileImagePreview(apiData.image);
      }
    }
  }, [profileResponse]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      console.error('❌ Profile fetch error:', error);
      console.log('🔍 Error details:', {
        status: error.status,
        data: error.data,
        originalStatus: error.originalStatus
      });
      
      if (error.status === 401) {
        setErrorMessage('Unauthorized. Please log in again.');
      } else if (error.status === 404) {
        setErrorMessage('Profile not found.');
      } else if (error.status === 500) {
        setErrorMessage('Server error. Please try again later.');
      } else if (error.status === 'PARSING_ERROR') {
        setErrorMessage('Invalid response from server.');
      } else if (error.status === 'FETCH_ERROR') {
        setErrorMessage('Network error. Please check your connection.');
      } else {
        setErrorMessage('Failed to load profile. Please try again.');
      }
    } else {
      setErrorMessage('');
    }
  }, [error]);

  const handleInputChange = (section, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleExpertiseAdd = () => {
    if (newExpertise.trim()) {
      // Get current expertise as array
      const currentExpertise = profileData.professional.areaOfExpertise
        ? profileData.professional.areaOfExpertise.split(',').map(e => e.trim()).filter(e => e)
        : [];
      
      // Add new expertise if not already present
      if (!currentExpertise.includes(newExpertise.trim())) {
        const updatedExpertise = [...currentExpertise, newExpertise.trim()];
        
        setProfileData(prev => ({
          ...prev,
          professional: {
            ...prev.professional,
            areaOfExpertise: updatedExpertise.join(', ')
          }
        }));
      }
      setNewExpertise('');
    }
  };

  const handleExpertiseRemove = (index) => {
    const currentExpertise = profileData.professional.areaOfExpertise
      ? profileData.professional.areaOfExpertise.split(',').map(e => e.trim()).filter(e => e)
      : [];
    
    const updatedExpertise = currentExpertise.filter((_, i) => i !== index);
    
    setProfileData(prev => ({
      ...prev,
      professional: {
        ...prev.professional,
        areaOfExpertise: updatedExpertise.join(', ')
      }
    }));
  };

  const handleExpertiseKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleExpertiseAdd();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Image size must be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select a valid image file (JPEG, PNG, etc.)');
        return;
      }

      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // CV Upload Handlers
  const handleCvClick = () => {
    cvFileInputRef.current?.click();
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const validExtensions = ['.pdf', '.doc', '.docx'];
      
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const isValidType = validTypes.includes(file.type) || validExtensions.includes(fileExtension);
      
      if (!isValidType) {
        setErrorMessage('Please upload only PDF or Word documents (.pdf, .doc, .docx)');
        return;
      }
      
      console.log('CV File selected:', file.name, file.size, file.type);
      setCvFile(file);
    }
  };

  const handleRemoveCv = () => {
    setCvFile(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      console.log('💾 Current profile data state:', profileData);
      
      const formData = new FormData();
      
      // Add all fields with proper validation - USING CORRECT FIELD NAMES
      const fields = {
        fname: profileData.personal.firstName?.trim() || '',
        lname: profileData.personal.lastName?.trim() || '',
        email: profileData.personal.email?.trim() || '',
        phoneNum: profileData.personal.phone?.trim() || '',
        bio: profileData.personal.bio?.trim() || '',
        yearsOfExperience: profileData.professional.yearsOfExperience?.toString() || '',
        website: profileData.professional.website?.trim() || '',
        videoUrl: profileData.professional.videoUrl?.trim() || ''
      };

      // Append all fields to FormData
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
        console.log(`📝 Appending ${key}:`, value);
      });

      // IMPORTANT: Handle subjects/department - Send as comma-separated string
      // API expects field name "department" not "subjects"
      if (profileData.professional.subjects) {
        formData.append('department', profileData.professional.subjects);
        console.log(`📝 Appending department:`, profileData.professional.subjects);
      }

      // IMPORTANT: Handle expertise - Send as comma-separated string
      // API expects field name "expertise" not "areaOfExpertise"
      if (profileData.professional.areaOfExpertise) {
        formData.append('expertise', profileData.professional.areaOfExpertise);
        console.log(`📝 Appending expertise:`, profileData.professional.areaOfExpertise);
      }

      // Handle image - only append if it's a new file
      if (profileImage && profileImage instanceof File) {
        formData.append('image', profileImage);
        console.log(`📝 Appending image:`, profileImage.name, profileImage.size, 'bytes');
      } else {
        // If no new image, send empty image field (as shown in curl)
        formData.append('image', '');
        console.log('📝 Appending empty image field');
      }

      // Handle CV - only append if it's a new file
      if (cvFile && cvFile instanceof File) {
        formData.append('cv', cvFile);
        console.log(`📝 Appending cv:`, cvFile.name, cvFile.size, 'bytes', cvFile.type);
      } else {
        // If no new CV, send empty cv field (as shown in curl: -F 'cv=')
        formData.append('cv', '');
        console.log('📝 Appending empty cv field');
      }

      // Log FormData contents for debugging
      console.log('📤 Final FormData contents:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: "${value}"`);
        }
      }

      console.log(`🔄 Updating profile using token-based authentication`);

      // Call the update API
      const response = await updateProfile(formData).unwrap();
      
      console.log('✅ Profile update response:', response);

      if (response.status === true) {
        setSuccessMessage(response.message || 'Profile updated successfully!');
        setIsEditing(false);
        setProfileImage(null); // Reset image state after successful upload
        setCvFile(null); // Reset CV state after successful upload
        
        // Refetch profile data to ensure we have the latest
        refetch();
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }

    } catch (error) {
      console.error('❌ Update error details:', {
        status: error.status,
        data: error.data,
        message: error.message
      });
      
      if (error.data) {
        const serverMessage = error.data.message || 'Server error occurred';
        setErrorMessage(`Server: ${serverMessage}`);
      } else if (error.status === 401) {
        setErrorMessage('Unauthorized. Please log in again.');
      } else if (error.status === 400) {
        setErrorMessage('Bad request. Please check all fields are filled correctly.');
      } else if (error.status === 500) {
        setErrorMessage('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setErrorMessage('Network error. Please check your connection.');
      } else {
        setErrorMessage(error.message || 'Failed to update profile. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setIsChangingPassword(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const { currentPassword, newPassword, confirmPassword } = profileData.security;

      // Validation
      if (!currentPassword || !newPassword || !confirmPassword) {
        setErrorMessage('Please fill in all password fields');
        return;
      }

      if (newPassword !== confirmPassword) {
        setErrorMessage('New passwords do not match');
        return;
      }

      if (newPassword.length < 8) {
        setErrorMessage('New password must be at least 8 characters long');
        return;
      }

      console.log('🔐 Changing password...');

      // Prepare password data with backend variable names (oldPassword instead of currentPassword)
      const passwordData = {
        oldPassword: currentPassword,
        newPassword: newPassword
      };

      console.log('📤 Sending password change request:', passwordData);

      // Call the change password API
      const response = await changePassword(passwordData).unwrap();
      
      console.log('✅ Password change response:', response);

      if (response.status === true) {
        setSuccessMessage(response.message || 'Password changed successfully!');
        
        // Clear password fields
        setProfileData(prev => ({
          ...prev,
          security: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }
        }));
      } else {
        throw new Error(response.message || 'Failed to change password');
      }

    } catch (error) {
      console.error('❌ Password change error:', error);
      if (error.data) {
        // Handle specific error messages from API
        if (error.data.message === 'Old password is incorrect') {
          setErrorMessage('Current password is incorrect');
        } else if (error.data.message === 'Unauthorized access') {
          setErrorMessage('Unauthorized. Please log in again.');
        } else if (error.data.message === 'Instructor not found') {
          setErrorMessage('User account not found');
        } else if (error.data.message === 'Something went wrong') {
          setErrorMessage('Server error. Please try again later.');
        } else {
          setErrorMessage(error.data.message || 'Failed to change password. Please try again.');
        }
      } else if (error.status === 401) {
        setErrorMessage('Unauthorized. Please log in again.');
      } else if (error.status === 400) {
        setErrorMessage('Invalid password data. Please check your input.');
      } else if (error.status === 404) {
        setErrorMessage('User account not found');
      } else if (error.status === 500) {
        setErrorMessage('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setErrorMessage('Network error. Please check your connection.');
      } else {
        setErrorMessage(error.message || 'Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleRetry = () => {
    setErrorMessage('');
    refetch();
  };

  // Show loading state for auth initialization
  if (!authInitialized) {
    return <SettingsLoading message="Initializing..." />;
  }

  // Show loading state
  if (isLoading) {
    return <SettingsLoading message="Loading your profile..." />;
  }

  // Show authentication error
  if (!isAuthenticated) {
    return (
      <SettingsLoading 
        type="error"
        title="Authentication Required"
        message="Please log in to view your profile."
        buttonText="Go to Login"
        onButtonClick={() => window.location.href = '/login'}
      />
    );
  }

  // Show error state only if we have an error and no data
  if (isError && !profileResponse) {
    return (
      <SettingsLoading 
        type="error"
        title="Unable to Load Profile"
        message={errorMessage}
        buttonText="Try Again"
        secondaryButtonText="Re-login"
        onButtonClick={handleRetry}
        onSecondaryButtonClick={() => window.location.href = '/login'}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <SettingsHeader />
        
        <SettingsMessages 
          successMessage={successMessage}
          errorMessage={errorMessage}
        />

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 ease-out hover:shadow-3xl">
          <div className="flex flex-col lg:flex-row">
            <SettingsSidebar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isEditing={isEditing}
              profileImagePreview={profileImagePreview}
              profileResponse={profileResponse}
              handleImageClick={handleImageClick}
              fileInputRef={fileInputRef}
              handleImageChange={handleImageChange}
              profileData={profileData}
              cvFile={cvFile}
              handleCvClick={handleCvClick}
              handleCvChange={handleCvChange}
              handleRemoveCv={handleRemoveCv}
              cvFileInputRef={cvFileInputRef}
            />

            {/* SettingsContent with increased height */}
            <div className="flex-1 p-8 overflow-y-auto" style={{ maxHeight: '800px', minHeight: '600px' }}>
              <SettingsContent
                activeTab={activeTab}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                profileImage={profileImage}
                setProfileImage={setProfileImage}
                profileResponse={profileResponse}
                setProfileImagePreview={setProfileImagePreview}
                isSaving={isSaving}
                isUpdating={isUpdating}
                handleSave={handleSave}
                isChangingPassword={isChangingPassword}
                isPasswordChanging={isPasswordChanging}
                handlePasswordChange={handlePasswordChange}
                profileData={profileData}
                handleInputChange={handleInputChange}
                newExpertise={newExpertise}
                setNewExpertise={setNewExpertise}
                handleExpertiseAdd={handleExpertiseAdd}
                handleExpertiseRemove={handleExpertiseRemove}
                handleExpertiseKeyPress={handleExpertiseKeyPress}
                cvFile={cvFile}
                handleCvClick={handleCvClick}
                handleCvChange={handleCvChange}
                handleRemoveCv={handleRemoveCv}
                cvFileInputRef={cvFileInputRef}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input for CV */}
      <input
        ref={cvFileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={handleCvChange}
        className="hidden"
      />

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
        .hover\\:shadow-3xl:hover {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
};

export default ProfileSettings;